import { CONSENT_EVENT_QUEUE_KEY } from './constants.js';
import { isConsentEventPayload } from './consent-event.js';
import type { ConsentEventPayload } from './types.js';
import { hasOwn, isCanonicalUtcTimestamp, isRecord } from './validation.js';

export const CONSENT_EVENT_QUEUE_SCHEMA_VERSION = 1;
export const MAX_CONSENT_EVENT_QUEUE_LENGTH = 50;
export const MAX_CONSENT_EVENT_QUEUE_BYTES = 65_536;
export const MAX_CONSENT_EVENT_AGE_MS = 7 * 24 * 60 * 60 * 1_000;

export interface QueuedConsentEvent {
  readonly payload: ConsentEventPayload;
  readonly enqueuedAt: string;
  readonly attempts: number;
  readonly nextAttemptAt: number;
}

interface StoredConsentEventQueue {
  readonly schemaVersion: typeof CONSENT_EVENT_QUEUE_SCHEMA_VERSION;
  readonly events: readonly QueuedConsentEvent[];
}

export interface ConsentEventQueue {
  read(): readonly QueuedConsentEvent[];
  enqueue(payload: ConsentEventPayload): QueuedConsentEvent;
  replaceHead(entry: QueuedConsentEvent): void;
  removeHead(clientEventId: string): void;
}

export type ConsentEventQueueErrorCode =
  | 'event_conflict'
  | 'queue_full'
  | 'storage_unavailable';

export class ConsentEventQueueError extends Error {
  readonly code: ConsentEventQueueErrorCode;
  readonly cause?: unknown;

  constructor(
    code: ConsentEventQueueErrorCode,
    message: string,
    options: { readonly cause?: unknown } = {},
  ) {
    super(message);
    this.name = 'ConsentEventQueueError';
    this.code = code;
    if (options.cause !== undefined) this.cause = options.cause;
  }
}

export interface ConsentEventQueueOptions {
  readonly storage?: Storage;
  readonly storageKey?: string;
  readonly now?: () => Date;
}

function isQueuedConsentEvent(value: unknown): value is QueuedConsentEvent {
  return (
    isRecord(value) &&
    Object.keys(value).length === 4 &&
    hasOwn(value, 'payload') &&
    hasOwn(value, 'enqueuedAt') &&
    hasOwn(value, 'attempts') &&
    hasOwn(value, 'nextAttemptAt') &&
    isConsentEventPayload(value.payload) &&
    isCanonicalUtcTimestamp(value.enqueuedAt) &&
    Number.isSafeInteger(value.attempts) &&
    (value.attempts as number) >= 0 &&
    Number.isSafeInteger(value.nextAttemptAt) &&
    (value.nextAttemptAt as number) >= 0
  );
}

function parseQueue(raw: string | null): readonly QueuedConsentEvent[] | null {
  if (raw === null) return [];
  if (new TextEncoder().encode(raw).byteLength > MAX_CONSENT_EVENT_QUEUE_BYTES) {
    return null;
  }

  try {
    const value: unknown = JSON.parse(raw);
    if (
      !isRecord(value) ||
      Object.keys(value).length !== 2 ||
      value.schemaVersion !== CONSENT_EVENT_QUEUE_SCHEMA_VERSION ||
      !Array.isArray(value.events) ||
      value.events.length > MAX_CONSENT_EVENT_QUEUE_LENGTH ||
      !value.events.every(isQueuedConsentEvent)
    ) {
      return null;
    }
    return value.events;
  } catch {
    return null;
  }
}

function freezeEntry(entry: QueuedConsentEvent): QueuedConsentEvent {
  return Object.freeze({
    ...entry,
    payload: Object.freeze({
      ...entry.payload,
      choices: Object.freeze({ ...entry.payload.choices }),
    }),
  });
}

export function createConsentEventQueue(
  options: ConsentEventQueueOptions = {},
): ConsentEventQueue {
  const storage = options.storage ?? globalThis.localStorage;
  const storageKey = options.storageKey ?? CONSENT_EVENT_QUEUE_KEY;
  const now = options.now ?? (() => new Date());

  function storageFailure(cause: unknown): ConsentEventQueueError {
    return new ConsentEventQueueError(
      'storage_unavailable',
      'Consent event queue storage is unavailable.',
      { cause },
    );
  }

  function persist(events: readonly QueuedConsentEvent[]): void {
    const stored: StoredConsentEventQueue = {
      schemaVersion: CONSENT_EVENT_QUEUE_SCHEMA_VERSION,
      events,
    };
    const serialized = JSON.stringify(stored);
    if (new TextEncoder().encode(serialized).byteLength > MAX_CONSENT_EVENT_QUEUE_BYTES) {
      throw new ConsentEventQueueError(
        'queue_full',
        'Consent event queue reached its byte limit.',
      );
    }

    try {
      if (events.length === 0) storage.removeItem(storageKey);
      else storage.setItem(storageKey, serialized);
    } catch (error) {
      throw storageFailure(error);
    }
  }

  function load(): QueuedConsentEvent[] {
    let raw: string | null;
    try {
      raw = storage.getItem(storageKey);
    } catch (error) {
      throw storageFailure(error);
    }

    const parsed = parseQueue(raw);
    if (parsed === null) {
      try {
        storage.removeItem(storageKey);
      } catch (error) {
        throw storageFailure(error);
      }
      return [];
    }

    const cutoff = now().getTime() - MAX_CONSENT_EVENT_AGE_MS;
    const retained = parsed.filter(
      (entry) => Date.parse(entry.enqueuedAt) >= cutoff,
    );
    if (retained.length !== parsed.length) persist(retained);
    return retained.map(freezeEntry);
  }

  return Object.freeze({
    read(): readonly QueuedConsentEvent[] {
      return Object.freeze(load());
    },

    enqueue(payload: ConsentEventPayload): QueuedConsentEvent {
      if (!isConsentEventPayload(payload)) {
        throw new TypeError('Only canonical consent events can be queued.');
      }

      const events = load();
      const duplicate = events.find(
        (entry) => entry.payload.clientEventId === payload.clientEventId,
      );
      if (duplicate) {
        if (JSON.stringify(duplicate.payload) !== JSON.stringify(payload)) {
          throw new ConsentEventQueueError(
            'event_conflict',
            'The client event ID is already associated with another payload.',
          );
        }
        return duplicate;
      }
      if (events.length >= MAX_CONSENT_EVENT_QUEUE_LENGTH) {
        throw new ConsentEventQueueError(
          'queue_full',
          'Consent event queue reached its item limit.',
        );
      }

      const current = now();
      if (Number.isNaN(current.getTime())) {
        throw new TypeError('Consent event queue clock returned an invalid Date.');
      }
      const entry = freezeEntry({
        payload,
        enqueuedAt: current.toISOString(),
        attempts: 0,
        nextAttemptAt: current.getTime(),
      });
      persist([...events, entry]);
      return entry;
    },

    replaceHead(entry: QueuedConsentEvent): void {
      if (!isQueuedConsentEvent(entry)) {
        throw new TypeError('Queue head replacement must be canonical.');
      }
      const events = load();
      if (
        events[0]?.payload.clientEventId !== entry.payload.clientEventId
      ) {
        throw new ConsentEventQueueError(
          'event_conflict',
          'Consent event queue head changed before replacement.',
        );
      }
      persist([freezeEntry(entry), ...events.slice(1)]);
    },

    removeHead(clientEventId: string): void {
      const events = load();
      if (events[0]?.payload.clientEventId !== clientEventId) {
        throw new ConsentEventQueueError(
          'event_conflict',
          'Consent event queue head changed before removal.',
        );
      }
      persist(events.slice(1));
    },
  });
}
